import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useActivity } from "@/context/ActivityContext";
import BackButton from "@/components/BackButton";
import { Radio, Cpu, ChevronRight, ShieldCheck, Users } from "lucide-react";

const CAT_IMG = {
  rc: "https://images.unsplash.com/photo-1613404198873-14a30017543a?crop=entropy&cs=srgb&fm=jpg&q=85",
  sim: "https://images.unsplash.com/photo-1743649978995-c76212449e15?crop=entropy&cs=srgb&fm=jpg&q=85",
};

export default function Championships() {
  const [champs, setChamps] = useState([]);
  const [params, setParams] = useSearchParams();
  const kind = params.get("kind") || "official";
  const cat = params.get("cat") || "all";
  const { tick } = useActivity();

  useEffect(() => {
    api.get(`/championships?kind=${kind}`).then((r) => setChamps(r.data)).catch(() => {});
  }, [kind, tick]);

  const filtered = cat === "all" ? champs : champs.filter((c) => c.category === cat);

  const setKind = (k) => setParams(k === "official" ? {} : { kind: k });
  const setCat = (c) => {
    const next = {};
    if (kind !== "official") next.kind = kind;
    if (c !== "all") next.cat = c;
    setParams(next);
  };

  const kindTabs = [
    { key: "official", label: "Ufficiali", icon: ShieldCheck },
    { key: "community", label: "Della Community", icon: Users },
  ];
  const catTabs = [
    { key: "all", label: "Tutti" },
    { key: "rc", label: "RC" },
    { key: "sim", label: "Simulatori" },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
      <BackButton className="mb-6" />
      <span className="text-xs font-bold tracking-[0.3em] uppercase text-cyan-400">Campionati</span>
      <h1 className="mt-3 font-heading text-4xl sm:text-5xl font-black tracking-tighter uppercase text-zinc-50">
        {kind === "official" ? "Campionati Ufficiali" : "Campionati della Community"}
      </h1>
      <p className="mt-3 text-zinc-400 text-sm">
        {kind === "official"
          ? "Serie ufficiali gestite dall'organizzazione Team Tuono."
          : "Campionati creati dagli utenti e approvati dall'amministrazione."}
      </p>

      <div className="mt-8 flex flex-wrap gap-2">
        {kindTabs.map((t) => (
          <button key={t.key} data-testid={`kind-${t.key}`} onClick={() => setKind(t.key)}
            className={`inline-flex items-center gap-2 px-5 py-3 text-xs font-bold tracking-[0.15em] uppercase transition-colors duration-200 ${
              kind === t.key ? "bg-yellow-400 text-zinc-950" : "border border-zinc-700 text-zinc-400 hover:text-zinc-100"
            }`}>
            <t.icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>

      <div className="mt-6 flex gap-2 border-b border-zinc-800">
        {catTabs.map((t) => (
          <button key={t.key} data-testid={`filter-${t.key}`} onClick={() => setCat(t.key)}
            className={`px-5 py-3 text-xs font-bold tracking-[0.15em] uppercase transition-colors duration-200 border-b-2 -mb-px ${
              cat === t.key ? "border-cyan-400 text-cyan-400" : "border-transparent text-zinc-400 hover:text-zinc-100"
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <p className="mt-16 text-center text-zinc-500" data-testid="empty-championships">
          Nessun campionato in questa sezione.
        </p>
      ) : (
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((c, i) => (
            <motion.div key={c.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
              <Link to={`/campionati/${c.id}`} data-testid={`championship-card-${c.id}`}
                className="group block h-full border border-zinc-800 bg-zinc-900 hover:border-yellow-400/50 transition-colors duration-200 overflow-hidden">
                <div className="relative h-40 overflow-hidden">
                  <img src={c.image_url || CAT_IMG[c.category]} alt={c.name}
                    className="w-full h-full object-cover opacity-60 group-hover:opacity-80 group-hover:scale-105 transition-all duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 to-transparent" />
                  <span className="absolute top-3 left-3 flex items-center gap-1.5 text-[10px] font-bold tracking-[0.15em] uppercase bg-zinc-950/80 text-cyan-400 px-2.5 py-1 rounded-full">
                    {c.category === "rc" ? <Radio className="h-3 w-3" /> : <Cpu className="h-3 w-3" />}
                    {c.category === "rc" ? "RC" : c.platform || "SIM"}
                  </span>
                </div>
                <div className="p-6">
                  <h3 className="font-heading text-xl font-bold text-zinc-50 tracking-tight">{c.name}</h3>
                  {c.description && <p className="mt-2 text-sm text-zinc-400 line-clamp-2">{c.description}</p>}
                  <div className="mt-4 flex items-center gap-1 text-xs font-bold tracking-[0.15em] uppercase text-yellow-400 group-hover:gap-2 transition-all duration-200">
                    Vedi classifica <ChevronRight className="h-4 w-4" />
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
